import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface RailExtensionTocChild {
  text: string;
  anchor: string;
}

export interface RailExtensionTocGroup {
  text: string;
  anchor: string;
  children: RailExtensionTocChild[];
}

export interface RailExtensionLink {
  href: string;
  label: string;
  /** Optional pre-rendered icon JSX. */
  icon?: ReactNode;
  /** Render the link in a dimmed style (e.g. for "new" placeholders). */
  dim?: boolean;
  /** Optional trailing badge text (e.g. "new"). */
  badge?: string;
}

/** One nested entry under a tree group — a leaf route (multi-page group) or an
 *  in-page section (a consolidated group's scroll-spy anchor). */
export interface RailExtensionTreeItem {
  href: string;
  label: string;
  /** Anchor id — present only for a consolidated group's in-page sections. When
   *  set the rail smooth-scrolls to it and scroll-spies the active section. */
  anchor?: string;
}

/** A top-level tree group — the right-rail mirror of a left-ModeRail mode. Its
 *  `items` reveal BELOW it in an animated accordion when the group is `active`. */
export interface RailExtensionTreeGroup {
  key: string;
  label: string;
  /** Group landing page. */
  href: string;
  /** Optional pre-rendered icon JSX (compared by reference; memoize the push). */
  icon?: ReactNode;
  /** This group is the active one → its accordion is open. */
  active: boolean;
  /** true → `items` are in-page anchors (scroll-spied); false → leaf routes. */
  consolidated: boolean;
  items: RailExtensionTreeItem[];
  /** For a multi-page active group: the href of the active leaf (route match), so
   *  the sliding marker lights without DOM scroll-spy. */
  activeItemHref?: string;
}

export interface RailExtension {
  /** Uppercase label for the sibling-docs group (e.g. the target name). */
  groupLabel: string;
  /** Sibling links rendered under groupLabel. */
  links: RailExtensionLink[];
  /** Slug/href that should render as the active link. */
  activeHref: string;
  /** On-this-page outline. H2/H3 only; H3s render as collapsible children. */
  toc: RailExtensionTocGroup[];
  /**
   * Which margin well renders this extension. `"left"` (default) folds it into
   * the in-flow LeftRail/SectionRail next to the primary ModeRail (product
   * settings use this). `"right"` floats it in the symmetric right-hand gutter
   * well via `ContextRail`, mirroring how ModeRail floats on the left — for
   * page-contextual reading nav (the docs sibling list + on-this-page TOC) that
   * should sit in the open right margin while the content stays centered.
   */
  side?: "left" | "right";
  /**
   * For a `side:"right"` extension only: the half-width (in rem) of the centered
   * body the rail floats beside. `ContextRail` places its left edge at
   * `calc(50% + tocOffsetRem)`, so this is how far past page-centre the well
   * starts. Defaults to `22` — the 22rem half of the 44rem reading column the
   * docs reader uses. A wider body (e.g. the analytics surface's ~58rem column)
   * passes a larger value so the floating well clears the content. Ignored when
   * `placement === "reserve"`.
   */
  tocOffsetRem?: number;
  /**
   * For a `side:"right"` extension: WHERE the rail floats.
   * - `"centered"` (default): float a wide rail in the open margin beside a
   *   *centered* reading column, starting at `calc(50% + tocOffsetRem)`. This is
   *   the docs reader — it narrows the body to make gutter room.
   * - `"reserve"`: pin a COMPACT rail in the right `px-36` gutter, symmetric with
   *   the left `ModeRail`, so the page body can stay FULL-WIDTH like every other
   *   page (no narrowing). Use when the outline labels are short.
   */
  placement?: "centered" | "reserve";
  /**
   * For a `placement:"reserve"` extension: the width (in rem) of the centered
   * content column the rail should HUG. The rail's left edge sits a constant
   * small gap to the right of that column's right edge on every viewport (and
   * floors to the screen edge when the gutter is too small), so a page-contextual
   * nav reads as belonging to the page instead of floating at the screen edge.
   * Defaults to `75` (the 1200px content well). A page with a narrower body
   * (settings = `max-w-3xl` = 48rem) passes its own width so the rail hugs the
   * real content.
   */
  reserveContentRem?: number;
  /**
   * For a `side:"right"` extension: an optional control node rendered ABOVE the
   * TOC in the rail (e.g. a time-period `<Select>`). The generic rail otherwise
   * carries only links + toc; this is the escape hatch for a page-level control
   * that belongs in the rail. Compared by REFERENCE in `sameRailExtension`, so
   * the caller must memoize it (else every render re-pushes).
   */
  controls?: ReactNode;
  /**
   * For a `side:"right"` extension: render a NESTED TREE instead of the flat
   * links + toc. Each group is a row; the ACTIVE group's `items` reveal beneath
   * it in an animated accordion (the left-ModeRail grammar mirrored on the
   * right). Settings uses this. When set, the rail renders the tree and ignores
   * `links`/`toc` (a consolidated active group's anchors are still scroll-spied).
   */
  tree?: RailExtensionTreeGroup[];
}

interface Ctx {
  extension: RailExtension | null;
  setExtension: (e: RailExtension | null) => void;
}

const RailExtensionContext = createContext<Ctx | null>(null);

export function RailExtensionProvider({ children }: { children: ReactNode }) {
  const [extension, setExtensionState] = useState<RailExtension | null>(null);
  // Guard against callers that hand us a fresh object identity every render
  // (e.g. inline `useMemo` whose deps include a `.filter()` result). Comparing
  // by deep-equality on the visible fields breaks the set→render→set loop.
  const setExtension = useCallback((e: RailExtension | null) => {
    setExtensionState((prev) => (sameRailExtension(prev, e) ? prev : e));
  }, []);
  const value = useMemo(() => ({ extension, setExtension }), [extension, setExtension]);
  return (
    <RailExtensionContext.Provider value={value}>
      {children}
    </RailExtensionContext.Provider>
  );
}

function sameRailExtension(a: RailExtension | null, b: RailExtension | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.groupLabel !== b.groupLabel || a.activeHref !== b.activeHref) return false;
  if ((a.side ?? "left") !== (b.side ?? "left")) return false;
  // Compare the float offset + placement too — else a re-push that only changes
  // the body measure (the well's start) or the centered/reserve mode would be
  // deduped and silently ignored.
  if ((a.tocOffsetRem ?? 22) !== (b.tocOffsetRem ?? 22)) return false;
  if ((a.reserveContentRem ?? 75) !== (b.reserveContentRem ?? 75)) return false;
  if ((a.placement ?? "centered") !== (b.placement ?? "centered")) return false;
  // Controls is an arbitrary node — compare by reference (callers memoize it).
  if (a.controls !== b.controls) return false;
  // Tree compared by structure (icons are nodes — ignored, callers rebuild them
  // each render but only when the deps that change the structure change).
  if (!sameRailTree(a.tree, b.tree)) return false;
  if (a.links.length !== b.links.length) return false;
  const bLinks = b.links;
  if (
    !a.links.every((x, i) => {
      const y = bLinks[i]!;
      return x.href === y.href && x.label === y.label && x.dim === y.dim && x.badge === y.badge;
    })
  ) {
    return false;
  }
  if (a.toc.length !== b.toc.length) return false;
  const bToc = b.toc;
  return a.toc.every((x, i) => {
    const y = bToc[i]!;
    if (x.text !== y.text || x.anchor !== y.anchor) return false;
    if (x.children.length !== y.children.length) return false;
    const yChildren = y.children;
    return x.children.every((cx, j) => {
      const cy = yChildren[j]!;
      return cx.text === cy.text && cx.anchor === cy.anchor;
    });
  });
}

/** Structural equality for the nested tree (icons excluded — compared elsewhere
 *  by reference). Returns true when both are absent. */
function sameRailTree(
  a: RailExtensionTreeGroup[] | undefined,
  b: RailExtensionTreeGroup[] | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((g, i) => {
    const h = b[i]!;
    if (
      g.key !== h.key ||
      g.label !== h.label ||
      g.href !== h.href ||
      g.active !== h.active ||
      g.consolidated !== h.consolidated ||
      g.activeItemHref !== h.activeItemHref
    ) {
      return false;
    }
    if (g.items.length !== h.items.length) return false;
    const hItems = h.items;
    return g.items.every((x, j) => {
      const y = hItems[j]!;
      return x.href === y.href && x.label === y.label && x.anchor === y.anchor;
    });
  });
}

export function useRailExtension(): RailExtension | null {
  const ctx = useContext(RailExtensionContext);
  return ctx?.extension ?? null;
}

/** Push a rail extension while this component is mounted. Clears on unmount.
 *  Pass `null` to clear without unmounting. */
export function useProvideRailExtension(extension: RailExtension | null): void {
  const ctx = useContext(RailExtensionContext);
  const set = ctx?.setExtension;

  const stableSet = useCallback(
    (e: RailExtension | null) => {
      set?.(e);
    },
    [set],
  );

  useEffect(() => {
    stableSet(extension);
    return () => stableSet(null);
  }, [stableSet, extension]);
}
